"use strict";
/**
 * Baseball Mode order buttons (offense hits + defense outs).
 * Button tables only — the two-stage wiring and order routing live in the
 * sport-generic ../sport/buy-buttons module.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupBaseballBuyButtons = setupBaseballBuyButtons;
exports.setupBaseballDefenseButtons = setupBaseballDefenseButtons;
const ui_1 = require("../../../ui");
const buy_buttons_1 = require("../sport/buy-buttons");
const pricing_1 = require("./pricing");
/** Look up the sport-generic metadata for a main baseball button element. */
function mainButtonMeta(btn) {
    if (!btn)
        return undefined;
    return pricing_1.BASEBALL_SPORT_BUTTONS.find((m) => m.btnId === btn.id);
}
/** Build the buy spec for one baseball button. */
function baseballSpec(btn, side, buttonId, getSlippage) {
    return { kind: "buy", btn, side, buttonId, getSlippage, meta: mainButtonMeta(btn) };
}
/** Build the additional-only (EXT) spec for one baseball button. */
function extSpec(btn, side, buttonId) {
    return { kind: "additional-only", btn, side, buttonId };
}
// ---------------------------------------------------------------------------
// Baseball buy buttons (offense)
// ---------------------------------------------------------------------------
/**
 * Wire up Baseball Mode buy buttons (Single / DBL / TRPL / HR) for both
 * sides. Each button computes its slippage dynamically from the current
 * baseball factor at click time and uses the same two-stage-confirm UX as
 * standard order buttons.
 *
 * @param bbButtons       - Map of baseball button DOM references
 * @param allButtons      - All standard order buttons (for mutual-exclusion reset)
 * @param baseballButtons - All sport-layout buttons (for mutual-exclusion reset)
 * @param armTimeoutId    - Shared arm-timeout ref
 * @param isConfirmRequired - Callback returning current two-stage-confirm state
 */
function setupBaseballBuyButtons(bbButtons, allButtons, baseballButtons, armTimeoutId, isConfirmRequired) {
    const hit = (h) => (factor) => (0, ui_1.getBaseballSlippage)(h, factor);
    const specs = [
        baseballSpec(bbButtons.miscTopA, "yes", "misc_zero_off", () => 0),
        extSpec(bbButtons.extA, "yes", "ext_off"),
        baseballSpec(bbButtons.singleA, "yes", "single", hit("single")),
        baseballSpec(bbButtons.dblA, "yes", "double", hit("double")),
        baseballSpec(bbButtons.trplA, "yes", "triple", hit("triple")),
        baseballSpec(bbButtons.hrA, "yes", "hr", hit("hr")),
        baseballSpec(bbButtons.miscBottomA, "yes", "misc_scaled_off", ui_1.getBaseballMiscSlippage),
        baseballSpec(bbButtons.miscTopB, "no", "misc_zero_off", () => 0),
        extSpec(bbButtons.extB, "no", "ext_off"),
        baseballSpec(bbButtons.singleB, "no", "single", hit("single")),
        baseballSpec(bbButtons.dblB, "no", "double", hit("double")),
        baseballSpec(bbButtons.trplB, "no", "triple", hit("triple")),
        baseballSpec(bbButtons.hrB, "no", "hr", hit("hr")),
        baseballSpec(bbButtons.miscBottomB, "no", "misc_scaled_off", ui_1.getBaseballMiscSlippage),
    ];
    (0, buy_buttons_1.setupSportBuyButtons)(specs, allButtons, baseballButtons, armTimeoutId, isConfirmRequired);
}
// ---------------------------------------------------------------------------
// Baseball defense buttons (outs)
// ---------------------------------------------------------------------------
/**
 * Wire up Baseball Mode defense buttons (Strikeout / Out / Double Play / Triple Play)
 * for both sides. Same two-stage-confirm UX as offensive buttons.
 */
function setupBaseballDefenseButtons(bbDefButtons, allButtons, baseballButtons, armTimeoutId, isConfirmRequired) {
    const out = (o) => (factor) => (0, ui_1.getBaseballDefenseSlippage)(o, factor);
    const specs = [
        baseballSpec(bbDefButtons.miscTopA, "yes", "misc_zero_def", () => 0),
        extSpec(bbDefButtons.extA, "yes", "ext_def"),
        baseballSpec(bbDefButtons.koA, "yes", "strikeout", out("strikeout")),
        baseballSpec(bbDefButtons.outA, "yes", "out", out("out")),
        baseballSpec(bbDefButtons.dpA, "yes", "double_play", out("double_play")),
        baseballSpec(bbDefButtons.tpA, "yes", "triple_play", out("triple_play")),
        baseballSpec(bbDefButtons.miscBottomA, "yes", "misc_scaled_def", ui_1.getBaseballMiscSlippage),
        baseballSpec(bbDefButtons.miscTopB, "no", "misc_zero_def", () => 0),
        extSpec(bbDefButtons.extB, "no", "ext_def"),
        baseballSpec(bbDefButtons.koB, "no", "strikeout", out("strikeout")),
        baseballSpec(bbDefButtons.outB, "no", "out", out("out")),
        baseballSpec(bbDefButtons.dpB, "no", "double_play", out("double_play")),
        baseballSpec(bbDefButtons.tpB, "no", "triple_play", out("triple_play")),
        baseballSpec(bbDefButtons.miscBottomB, "no", "misc_scaled_def", ui_1.getBaseballMiscSlippage),
    ];
    (0, buy_buttons_1.setupSportBuyButtons)(specs, allButtons, baseballButtons, armTimeoutId, isConfirmRequired);
}
